import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { GlobalNotification } from '../../model'
import { Resources } from '../../model/RBAC/constants'

import { UserPermission } from '@globaleris/ui.library'
import { MapToArray } from '../../helpers/arrayHelper/arrayHelper'
import { enumToMapValues, enumValueToKey } from '../../helpers/enumHelper'
import { hasPermission } from '../../helpers/permissionsHelper'

import { usePageState } from '../../hooks/usePageState'
import {
  getActiveCommodities,
  getActiveCommodity,
  storeActiveCommodity,
  storeActiveTabForCurrentPage,
  storeCommodityFilterForCurrentPage
} from '../../redux'

import Page from '../../components/page/page'
import PageHeader from '../../components/page/pageHeader'
import CommodityFilter from '../../components/shared/commodityFilter/commodityFilter'
import TabsWithPermissions, { Tab } from '../../components/tabsWithPermissions/tabsWithPermissions'

import { Grid } from '@material-ui/core'
import { EnumCommodity } from '../../api'
import { TabsPriceType } from './pricing.Model'

import { PricingTable } from './components/pricingTable/pricingTable'

export interface PricingPageProps {}

export const PricingPage: React.FC<PricingPageProps> = props => {
  const [t] = useTranslation(['pricing', 'regulatoryReports', 'globalerisLibrary', 'enums'])

  const {
    dataTableFilter,
    dataTableSearch,
    activeTab,
    commodityFilter: activeCommodity
  } = usePageState()

  const [globalNotification, setGlobalNotification] = useState<GlobalNotification | undefined>(
    undefined
  )

  const [isFetching, setIsFetching] = useState<boolean>(true)

  const [currentTab, setCurrentTab] = useState<number>(activeTab ?? 0)
  const [tabsDefinition, setTabsDefinition] = useState<Map<any, Tab>>(new Map())
  const [filteredTabsArray, setFilteredTabsArray] = useState<Tab[]>([])

  const commodities = getActiveCommodities()
  const initialCommodityFilter: EnumCommodity =
    getActiveCommodity() ??
    (commodities !== undefined && commodities?.length > 0
      ? enumValueToKey(EnumCommodity, commodities[0])
      : EnumCommodity.GasNatural)

  const commodity = activeCommodity ?? initialCommodityFilter

  // We set an Array from the PriceTypes in order to have arranged both index (table tabs index, starting in 0) and values corresponding to each each priceType.
  const PricingTabsArray = MapToArray(enumToMapValues(TabsPriceType))

  // Handlers Region Starts

  const isTabAllowedForCommodity = (commodity: EnumCommodity, tabValue: number): boolean => {
    const tabsNotPermittedForGas = [TabsPriceType.apuntamiento, TabsPriceType.parameterPeriod];

    if (commodity === EnumCommodity.GasNatural && tabsNotPermittedForGas.includes(tabValue)) {
      return false;
    }

    return true;
  };

  const handleTabPermission = (tabValue: number) => {
    if (!isTabAllowedForCommodity(commodity, tabValue)) {
      return false;
    }
    return hasPermission(Resources.Pricing, [UserPermission.EDIT, UserPermission.READ_ONLY]);
  }

  const onSelectCommodityHandler = (commodity: EnumCommodity): void => {
    storeActiveTabForCurrentPage(0)
    storeCommodityFilterForCurrentPage(commodity)
    storeActiveCommodity(commodity)
  }

  const onTableFetching = (childIsFetching: boolean) => {
    setIsFetching(childIsFetching)
  }


  
  const onTabChangeHandler = (tab: number) => {
    if (currentTab !== tab) {
      setIsFetching(true)
      setCurrentTab(tab)
      storeActiveTabForCurrentPage(tab)
      setGlobalNotification(undefined)
    }
  }

  // Handlers Region Ends

  // Functions Region Starts

  const renderTabContent = (activeTab: number) => {
    return (
      filteredTabsArray[activeTab]?.id && (
        <PricingTable
          priceType={filteredTabsArray[activeTab]?.id}
          tabType={filteredTabsArray[activeTab]?.id}
          commodity={commodity}
          dataTableFilter={dataTableFilter}
          dataTableSearch={dataTableSearch}
          onFetching={onTableFetching}
          isTabAllowedForCommodity={isTabAllowedForCommodity}
        ></PricingTable>
      )
    )
  }

  const renderCommodities = () => {
    if (commodities !== undefined && commodities.length > 1) {
      return (
        <CommodityFilter
          commodities={[
            { commodity: enumValueToKey(EnumCommodity, commodities[0]) },
            { commodity: enumValueToKey(EnumCommodity, commodities[1]) }
          ]}
          commodity={commodity}
          enabled={!isFetching}
          inactive={false}
          onSelectCommodity={onSelectCommodityHandler}
        />
      )
    }
    return
  }

  // Functions Region Ends

  // Hooks Region Starts

  useEffect(() => {
    if (activeTab !== undefined) {
      setCurrentTab(activeTab)
    } else {
      storeActiveTabForCurrentPage(0)
    }
  }, [activeTab])

  useEffect(() => {
    const previousTabId = filteredTabsArray[currentTab]?.id

    const newTabs = new Map(
      PricingTabsArray.map((tab, tabIndex) => [
        tabIndex,
        {
          label: t(`pricing:tabs.${tab.name}`),
          hasPermission: handleTabPermission(tab.value),
          id: tab.value
        }
      ])
    )

    const newFilteredTabsArray = Array.from(newTabs.values()).filter(tab => tab.hasPermission)

    setTabsDefinition(newTabs)
    setFilteredTabsArray(newFilteredTabsArray)

    if (previousTabId !== undefined) {
      const newTabIndex = newFilteredTabsArray.findIndex(tab => tab.id === previousTabId)
      if (newTabIndex !== -1) {
        // Tab anterior existe en la nueva lista -> mantenemos el tab equivalente
        setCurrentTab(newTabIndex)
        storeActiveTabForCurrentPage(newTabIndex)
      } else {
        // No existe -> nos quedamos con tab 0
        setCurrentTab(0)
        storeActiveTabForCurrentPage(0)
      }
    }
  }, [commodity])

  // Hooks Region Ends

  return (
    <Page notification={globalNotification}>
      <>
        <PageHeader title={t('pricing:title')}>
          <div>
            <Grid container justify="flex-end">
              {renderCommodities()}
            </Grid>
          </div>
        </PageHeader>
        <TabsWithPermissions
          tabs={tabsDefinition}
          tabDefault={currentTab}
          onTabChange={onTabChangeHandler}
          hideTabs={isFetching}
        />
        {renderTabContent(activeTab ? activeTab : currentTab)}
      </>
    </Page>
  )
}
